// ./src/components/features/Rentals/VendorSelect/VendorSelect.tsx
//
// Picks the vendor a rental (or rental asset) is rented from. The vendor is a
// venue row of kind='vendor'; this control lists those and lets the user create a
// new vendor in place (VenueEditDrawer kind=VENDOR) without leaving the drawer.
// Mirrors the venue picker inside TaskLocationFields.
//
// Self-contained: wraps its own VenuesProvider so the in-place VenueEditDrawer's
// create lands in the same list. Emits (id, label) — the label is a snapshot only.

import React, { useMemo, useState } from "react";
import { Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VenuesProvider, VenueCategoriesProvider, useVenuesContext } from "@/contexts";
import { Venue, VENUE_KIND, WHOLE_SET } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { VenueEditDrawer } from "@/components/features/Venues";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

export interface VendorSelectProps {
  value: string | null;
  onChange: (id: string | null, label: string | null) => void;
  /** Snapshot label to show for a vendor that isn't in the loaded list yet. */
  fallbackLabel?: string | null;
  label?: string;
  description?: string;
  disabled?: boolean;
  error?: React.ReactNode;
}

const VendorSelectInner: React.FC<VendorSelectProps> = ({
  value,
  onChange,
  fallbackLabel,
  label,
  description,
  disabled,
  error,
}) => {
  const { t } = useTranslation(["rentals", "common"]);
  const { venues, loading } = useVenuesContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // #142 — may this person add one here? (never for a crew member: it would land in their OWN account).
  const canCreate = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);

  const vendorData = useMemo(() => {
    const items = venues
      .filter((v) => v.kind === VENUE_KIND.VENDOR && !!v.id)
      .map((v) => ({ value: v.id as string, label: v.name || t("rentals:vendor.untitled", { defaultValue: "Untitled vendor" }) }));

    // Keep the selected vendor visible even if it hasn't loaded into the list.
    if (value && !items.some((o) => o.value === value)) {
      items.unshift({ value, label: fallbackLabel || t("rentals:vendor.selected", { defaultValue: "Selected vendor" }) });
    }

    return items;
  }, [venues, value, fallbackLabel, t]);

  const labelFor = (id: string): string | null => venues.find((v) => v.id === id)?.name ?? null;

  const handleChange = (id: string | null) => {
    if (!id) {
      onChange(null, null);

      return;
    }

    onChange(id, labelFor(id));
  };

  const handleCreated = (venue: Venue) => {
    setDrawerOpen(false);

    if (venue.id) {
      onChange(venue.id, venue.name || null);
    }
  };

  return (
    <>
      <SelectField
        label={label ?? t("rentals:vendor.label", { defaultValue: "Vendor" })}
        description={description}
        placeholder={loading ? t("common:loading", { defaultValue: "Loading…" }) : t("rentals:vendor.placeholder", { defaultValue: "Pick a vendor" })}
        data={vendorData}
        value={value}
        onChange={(_n, id) => handleChange(id)}
        createLabel={canCreate ? t("rentals:vendor.create", { defaultValue: "Add a new vendor" }) : undefined}
        onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
        searchable
        clearable
        disabled={disabled}
        error={error}
        nothingFoundMessage={t("rentals:vendor.empty", { defaultValue: "No vendors yet — add one" })}
        leftSection={<AppIcon iconName="Briefcase" size={16} />}
        rightSection={loading ? <Loader size="md" /> : undefined}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        size="md"
      />

      <VenueEditDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} kind={VENUE_KIND.VENDOR} onCreated={handleCreated} />
    </>
  );
};

export const VendorSelect: React.FC<VendorSelectProps> = (props) => {
  return (
    <VenuesProvider pageSize={WHOLE_SET}>
      {/* The in-place VenueEditDrawer's category picker reads from this provider. */}
      <VenueCategoriesProvider>
        <VendorSelectInner {...props} />
      </VenueCategoriesProvider>
    </VenuesProvider>
  );
};

export default VendorSelect;
